import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import {
  ArrowUp,
  Camera,
  ChevronDown,
  ClipboardPaste,
  ExternalLink,
  FileUp,
  Image as ImageIcon,
  Images,
  LogOut,
  Plus,
  SquarePen,
  User,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { initializeAmplify } from './auth/intitalizeCognito';
import { rememberPostAuthPath } from './auth/postAuthRedirect';
import LensClient from './clients/lensClient';
import { resolveLensSettings } from './config/apiConfig';
import { LENS_MODEL_OPTIONS, lensModelOptionsForStage } from './config/lensConfig';
import { getRegion, getStage } from './helpers/appUtils';
import { groundingSources } from './helpers/lensUtils';
import './Lens.css';

export { LENS_MODEL_OPTIONS } from './config/lensConfig';

const APP_TITLE = 'IMAX Web Lens';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_EDGE = 1568;
const MAX_QUERY_LENGTH = 2000;
const JPEG_QUALITIES = [0.8, 0.7, 0.6];
const ANSWER_MODES = [
  { key: 'mmir', label: 'Answer Tool with MMIR' },
  { key: 'web', label: 'Answer Tool' },
];

const SUGGESTIONS = [
  {
    title: 'Identify this landmark',
    subtitle: 'and tell me its history',
    prompt: 'Identify this landmark and tell me its history.',
  },
  {
    title: 'Where was this photo taken?',
    subtitle: 'find the location',
    prompt: 'Where was this photo taken? Find the location.',
  },
  {
    title: 'Who or what is this?',
    subtitle: 'identify the person or object',
    prompt: 'Who or what is this? Identify the person or object.',
  },
  {
    title: 'What happened here?',
    subtitle: 'find the news story',
    prompt: 'What happened here? Find the news story.',
  },
  {
    title: 'What breed is this?',
    subtitle: 'and how to care for it',
    prompt: 'What breed is this, and how do I care for it?',
  },
];

function decodedDataUrlBytes(dataUrl) {
  const base64 = dataUrl.split(',')[1] || '';
  return Math.floor((base64.length * 3) / 4);
}

function loadBrowserImage(url) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The selected image could not be read.'));
    image.src = url;
  });
}

function canvasDataUrl(image, maxEdge, quality) {
  const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('This browser cannot prepare the image.');
  }
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Re-encodes the selected image before it leaves the browser. Besides bounding
 * its size, drawing fresh pixels strips EXIF/XMP metadata such as GPS location.
 */
export async function prepareImageFile(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Choose an image file.');
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const source = await loadBrowserImage(objectUrl);
    let dataUrl = '';
    for (const quality of JPEG_QUALITIES) {
      dataUrl = canvasDataUrl(source, MAX_IMAGE_EDGE, quality);
      if (decodedDataUrlBytes(dataUrl) <= MAX_IMAGE_BYTES) {
        const thumbnail = canvasDataUrl(source, 720, 0.72);
        return { dataUrl, thumbnail, name: file.name || 'Selected photo' };
      }
    }
    throw new Error('Choose an image smaller than 5 MB after processing.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function answerText(response) {
  const answer = response?.answerWithCitations
    || response?.final_text
    || response?.mmlm_answer
    || response?.answer
    || response?.answer_with_vtag
    || response?.left?.answer;
  if (typeof answer === 'string') {
    return answer
      // AgentCore citation IDs are internal source pointers. Sources are
      // rendered separately in the Sources tray, so never leak these tokens
      // into customer-visible Markdown.
      .replace(/\uE200cite\uE202[\s\S]*?\uE201/g, '')
      .replace(/\[cite_[^\]]+\]/gi, '')
      .replace(/\bcite_[a-z0-9_-]+(?:\.[a-z0-9_-]+)+\b/gi, '')
      .replace(/[ \t]+([,.;:!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n[ \t]+/g, '\n')
      .trim();
  }
  return answer ? JSON.stringify(answer, null, 2) : '';
}

function LoadingDots() {
  return (
    <div className="lens-assistant-row" aria-label="Thinking" role="status">
      <div className="lens-dots">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function sourceLabel(source) {
  return source.domain || source.title || 'Grounding source';
}

function sourceInitials(source) {
  const label = sourceLabel(source).replace(/^www\./, '');
  return label
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'VS';
}

function GroundingPreview({ source, compact = false }) {
  const [failed, setFailed] = useState(false);
  const previewUrl = source.previewUrl;
  return (
    <span className={`lens-grounding-preview ${compact ? 'lens-grounding-preview-compact' : ''}`}>
      {!failed && previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="lens-grounding-fallback" aria-hidden="true">
          {sourceInitials(source)}
        </span>
      )}
    </span>
  );
}

function SourcesTray({ grounding, onOpen }) {
  const previewSources = [
    ...grounding.reverseImageSearch,
    ...grounding.webSearch,
    ...grounding.vtagWeb,
  ];
  return (
    <button
      className="lens-sources-tray"
      type="button"
      aria-label={`Open ${grounding.total} grounding sources`}
      onClick={onOpen}
    >
      <span className="lens-source-stack" aria-hidden="true">
        {previewSources.slice(0, 3).map((source, index) => (
          <GroundingPreview
            source={source}
            compact
            key={`${source.url || source.sourceUrl}-${index}`}
          />
        ))}
      </span>
      <span className="lens-sources-copy">
        <strong>Sources</strong>
        <span>
          {grounding.webSearch.length} web search · {grounding.vtagWeb.length + grounding.reverseImageSearch.length} visual QA
        </span>
      </span>
      <span className="lens-sources-arrow" aria-hidden="true">↑</span>
    </button>
  );
}

function MediaImageCard({ image }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  const body = (
    <>
      <img
        src={image.imageUrl}
        alt={image.title}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
      <span>{image.title}</span>
    </>
  );
  return image.sourceUrl ? (
    <a
      className="lens-media-image"
      href={image.sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {body}
    </a>
  ) : <div className="lens-media-image">{body}</div>;
}

function InlineMedia({ media, status }) {
  if (status === 'loading') {
    return (
      <div className="lens-media-loading" role="status">
        <span />
        Finding useful images…
      </div>
    );
  }
  if (!media?.images?.length) return null;
  return (
    <div className="lens-inline-media">
      {media.images?.length > 0 && (
        <section className="lens-media-section">
          <div className="lens-media-rail lens-media-image-rail" aria-label="Related images">
            {media.images.map((image) => (
              <MediaImageCard image={image} key={image.id} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function AnswerWithMedia({ answer, media, status }) {
  const sections = Array.isArray(media?.sections) ? media.sections : [];
  const sectionMedia = sections.reduce((result, section) => {
    if (!Array.isArray(section?.items)) return result;
    if (section.type === 'images') result.images.push(...section.items);
    return result;
  }, { images: [] });
  const renderedMedia = sections.length ? sectionMedia : media;

  return (
    <>
      <div className="lens-answer"><ReactMarkdown>{answer}</ReactMarkdown></div>
      <InlineMedia media={renderedMedia} status={status} />
    </>
  );
}

function GroundingCard({ source, index }) {
  const href = source.sourceUrl || source.url;
  const body = (
    <>
      {source.previewUrl && <GroundingPreview source={source} />}
      <span className="lens-grounding-card-copy">
        <span className="lens-source-rank">Source {index + 1}</span>
        <strong>{source.title}</strong>
        {source.snippet && <span className="lens-source-snippet">{source.snippet}</span>}
        <span>{sourceLabel(source)}</span>
      </span>
      {href && <ExternalLink size={18} aria-hidden="true" />}
    </>
  );
  const className = `lens-grounding-card${source.previewUrl ? '' : ' lens-grounding-card-text'}`;
  return href ? (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      key={`${href}-${index}`}
    >
      {body}
    </a>
  ) : (
    <div className={className} key={`source-${index}`}>{body}</div>
  );
}

function GroundingPassageCard({ source, index }) {
  const href = source.sourceUrl || source.url;
  return (
    <article className="lens-grounding-passage">
      <span className="lens-source-rank">Grounding passage {index + 1}</span>
      <p>{source.snippet || source.title}</p>
      <div className="lens-grounding-passage-source">
        <span>
          <strong>{source.title}</strong>
          <small>{sourceLabel(source)}</small>
        </span>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open source for grounding passage ${index + 1}`}
          >
            Source
            <ExternalLink size={15} aria-hidden="true" />
          </a>
        )}
      </div>
    </article>
  );
}

function GroundingSection({
  title,
  subtitle,
  sources,
  presentation = 'sources',
}) {
  if (!sources.length) return null;
  return (
    <section className="lens-grounding-section">
      <div className="lens-grounding-section-heading">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      <div className="lens-grounding-list">
        {sources.map((source, index) => (
          presentation === 'passages' ? (
            <GroundingPassageCard
              source={source}
              index={index}
              key={`${source.sourceUrl || source.url}-${index}`}
            />
          ) : (
            <GroundingCard
              source={source}
              index={index}
              key={`${source.sourceUrl || source.url}-${index}`}
            />
          )
        ))}
      </div>
    </section>
  );
}

function GroundingSheet({ grounding, onClose }) {
  const visualWebSources = grounding.vtagWeb;
  const visualSourceCount = grounding.reverseImageSearch.length + visualWebSources.length;
  const [tab, setTab] = useState(
    grounding.webSearch.length || grounding.searches.length ? 'web' : 'visual',
  );
  const [visualTab, setVisualTab] = useState(
    grounding.reverseImageSearch.length ? 'reverse-image' : 'web',
  );

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div className="lens-grounding-layer">
      <button
        className="lens-modal-scrim"
        type="button"
        aria-label="Close grounding sources"
        onClick={onClose}
      />
      <section
        className="lens-grounding-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Grounding sources"
      >
        <div className="lens-sheet-grabber" aria-hidden="true" />
        <div className="lens-grounding-header">
          <div>
            <span className="lens-eyebrow">Answer evidence</span>
            <h2>Sources</h2>
          </div>
          <button type="button" aria-label="Close grounding sources" onClick={onClose}>
            <X size={22} />
          </button>
        </div>
        <div className="lens-grounding-tabs" role="tablist" aria-label="Grounding source type">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'web'}
            className={tab === 'web' ? 'lens-grounding-tab-active' : ''}
            onClick={() => setTab('web')}
          >
            Web Search Tool
            <span>{grounding.webSearch.length}</span>
          </button>
          {visualSourceCount > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'visual'}
              className={tab === 'visual' ? 'lens-grounding-tab-active' : ''}
              onClick={() => setTab('visual')}
            >
              Visual QA Tool
              <span>{visualSourceCount}</span>
            </button>
          )}
        </div>
        <div className="lens-grounding-body">
          {tab === 'web' ? (
            <>
              <GroundingSection
                title="Web search"
                subtitle="Pages searched by the Answer Tool"
                sources={grounding.webSearch}
              />
              {grounding.webSearch.length === 0 && (
                <div className="lens-grounding-empty">
                  <strong>Search result details are not available yet.</strong>
                  {grounding.searches.length > 0 && (
                    <>
                      <span>The Answer Tool ran:</span>
                      <ul>
                        {grounding.searches.map((search, index) => (
                          <li key={`${search.query}-${index}`}>{search.query}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div
                className="lens-grounding-subtabs"
                role="tablist"
                aria-label="Visual QA evidence type"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={visualTab === 'reverse-image'}
                  className={visualTab === 'reverse-image' ? 'lens-grounding-subtab-active' : ''}
                  onClick={() => setVisualTab('reverse-image')}
                >
                  Reverse-image search
                  <span>{grounding.reverseImageSearch.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={visualTab === 'web'}
                  className={visualTab === 'web' ? 'lens-grounding-subtab-active' : ''}
                  onClick={() => setVisualTab('web')}
                >
                  Web
                  <span>{visualWebSources.length}</span>
                </button>
              </div>
              {visualTab === 'reverse-image' ? (
                <GroundingSection
                  title="Reverse-image search"
                  subtitle="Visually similar pages returned by the Visual QA Tool"
                  sources={grounding.reverseImageSearch}
                />
              ) : (
                <GroundingSection
                  title="Image-grounded text"
                  subtitle="Grounding passages selected after the Visual QA Tool understood the image"
                  sources={visualWebSources}
                  presentation="passages"
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function AnswerModePill({ activeMode, disabled, onSelect }) {
  return (
    <div className="lens-answer-mode" role="tablist" aria-label="Answer grounding">
      {ANSWER_MODES.map((option) => (
        <button
          type="button"
          role="tab"
          aria-selected={activeMode === option.key}
          className={activeMode === option.key ? 'lens-answer-mode-active' : ''}
          disabled={disabled}
          key={option.key}
          onClick={() => onSelect(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function AssistantTurn({
  turn,
  onRetry,
  onSignInAgain,
  onOpenSources,
  onSelectMode,
}) {
  const activeMode = turn.activeMode || 'mmir';
  const response = turn.responses?.[activeMode] || turn.response;
  const status = turn.modeStatus?.[activeMode] || turn.status;
  const error = turn.modeErrors?.[activeMode] || turn.error;
  const needsReauth = turn.modeNeedsReauth?.[activeMode] || turn.needsReauth;
  const grounding = groundingSources(response);
  const answer = answerText(response);
  return (
    <div className="lens-assistant-row">
      <AnswerModePill
        activeMode={activeMode}
        disabled={false}
        onSelect={onSelectMode}
      />
      {status === 'loading' && <LoadingDots />}
      {status === 'error' && (
        <div className="lens-assistant-bubble lens-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={needsReauth ? onSignInAgain : onRetry}>
            {needsReauth ? 'Sign in again' : 'Retry'}
          </button>
        </div>
      )}
      {status === 'answer' && (
        answer
          ? <AnswerWithMedia answer={answer} media={response?.media} status="done" />
          : <div className="lens-answer"><p>No answer this time. Try asking again.</p></div>
      )}
      {status === 'answer' && grounding.total > 0 && (
        <SourcesTray
          grounding={grounding}
          onOpen={() => onOpenSources(grounding)}
        />
      )}
    </div>
  );
}

export function LensExperience({
  alias,
  client,
  onSignOut,
  onSignInAgain,
  modelOptions = LENS_MODEL_OPTIONS,
}) {
  const [query, setQuery] = useState('');
  const [image, setImage] = useState(null);
  const [lastImage, setLastImage] = useState(null);
  const [turns, setTurns] = useState([]);
  const [hint, setHint] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [model, setModel] = useState(modelOptions[0]?.key || 'sonnet');
  const [answerMode, setAnswerMode] = useState('mmir');
  const [preparing, setPreparing] = useState(false);
  const [activeSources, setActiveSources] = useState(null);
  const [dragging, setDragging] = useState(false);
  const conversation = useRef(null);
  const nextTurnId = useRef(1);
  const loading = turns.some((turn) => (
    turn.modeStatus
      ? Object.values(turn.modeStatus).includes('loading')
      : turn.status === 'loading'
  ));
  const latestTurn = turns.length > 0 ? turns[turns.length - 1] : null;
  const latestTurnId = latestTurn ? latestTurn.id : null;
  const latestActiveMode = latestTurn?.activeMode || 'mmir';
  const latestVisibleStatus = latestTurn?.modeStatus?.[latestActiveMode]
    || latestTurn?.status;
  const latestVisibleResponse = latestTurn?.responses?.[latestActiveMode]
    || latestTurn?.response;
  const effectiveImage = image || lastImage;
  const canSend = Boolean(
    effectiveImage
    && query.trim()
    && query.trim().length <= MAX_QUERY_LENGTH
    && !loading
    && !preparing,
  );

  useEffect(() => {
    const element = conversation.current;
    if (latestTurnId && element && element.scrollHeight > element.clientHeight) {
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }
  }, [
    latestTurnId,
    latestActiveMode,
    latestVisibleResponse,
    latestVisibleStatus,
  ]);

  const ingestImage = useCallback(async (file) => {
    if (!file) return;
    setHint('');
    setPreparing(true);
    try {
      setImage(await prepareImageFile(file));
      setSheetOpen(false);
    } catch (error) {
      setHint(error?.message || 'Could not load the image. Try a different photo.');
    } finally {
      setPreparing(false);
    }
  }, []);

  const chooseImage = useCallback((event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    void ingestImage(file);
  }, [ingestImage]);

  const pasteImage = useCallback((event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const item = items.find((candidate) => candidate.type?.startsWith('image/'));
    const files = Array.from(event.clipboardData?.files || []);
    const file = item?.getAsFile?.() || files.find((candidate) => candidate.type?.startsWith('image/'));
    if (!file) return;
    event.preventDefault();
    void ingestImage(file);
  }, [ingestImage]);

  const readClipboardImage = useCallback(async () => {
    setHint('');
    try {
      if (!navigator.clipboard?.read) {
        throw new Error('Clipboard image access is not available in this browser.');
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types?.find((type) => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], 'clipboard-image', { type: imageType });
          await ingestImage(file);
          return;
        }
      }
      throw new Error('There is no image on the clipboard.');
    } catch (error) {
      setSheetOpen(false);
      setHint(`${error?.message || 'Could not read the clipboard'} Long-press the composer to paste instead.`);
    }
  }, [ingestImage]);

  const dropImage = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    const file = Array.from(event.dataTransfer?.files || [])
      .find((candidate) => candidate.type?.startsWith('image/'));
    if (file) void ingestImage(file);
  }, [ingestImage]);

  const updateTurn = useCallback((id, patch) => {
    setTurns((previous) => previous.map((turn) => (turn.id === id ? { ...turn, ...patch } : turn)));
  }, []);

  const runAnswerMode = useCallback(async (
    id,
    text,
    photo,
    selectedModel,
    selectedAnswerMode,
  ) => {
    try {
      const response = await client.ask({
        query: text,
        imageDataUrl: photo.dataUrl,
        model: selectedModel,
        answerMode: selectedAnswerMode,
      });
      if (response?.statusCode && response.statusCode >= 400) {
        const needsReauth = response.statusCode === 401;
        setTurns((previous) => previous.map((turn) => (
          turn.id === id
            ? {
              ...turn,
              modeStatus: { ...turn.modeStatus, [selectedAnswerMode]: 'error' },
              modeErrors: {
                ...turn.modeErrors,
                [selectedAnswerMode]: needsReauth
                  ? 'Your session has expired. Please sign in again.'
                  : response.message || `The service returned an error (${response.statusCode}).`,
              },
              modeNeedsReauth: {
                ...turn.modeNeedsReauth,
                [selectedAnswerMode]: needsReauth,
              },
            }
            : turn
        )));
        return;
      }
      setTurns((previous) => previous.map((turn) => {
        if (turn.id !== id) return turn;
        return {
          ...turn,
          modeStatus: { ...turn.modeStatus, [selectedAnswerMode]: 'answer' },
          responses: {
            ...(turn.responses || {}),
            [selectedAnswerMode]: response,
          },
          modeErrors: { ...turn.modeErrors, [selectedAnswerMode]: '' },
          modeNeedsReauth: {
            ...turn.modeNeedsReauth,
            [selectedAnswerMode]: false,
          },
        };
      }));
    } catch (error) {
      setTurns((previous) => previous.map((turn) => (
        turn.id === id
          ? {
            ...turn,
            modeStatus: { ...turn.modeStatus, [selectedAnswerMode]: 'error' },
            modeErrors: {
              ...turn.modeErrors,
              [selectedAnswerMode]: error?.message
                || 'Could not reach the service. Check your connection and try again.',
            },
            modeNeedsReauth: {
              ...turn.modeNeedsReauth,
              [selectedAnswerMode]: false,
            },
          }
          : turn
      )));
    }
  }, [client]);

  const submit = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }
    if (!canSend) return;
    const id = nextTurnId.current++;
    const text = query.trim();
    const photo = effectiveImage;
    setTurns((previous) => [
      ...previous,
      {
        id,
        query: text,
        thumbnail: photo.thumbnail,
        status: 'loading',
        response: null,
        error: '',
        needsReauth: false,
        retryImage: photo,
        model,
        activeMode: answerMode,
        responses: {},
        modeStatus: {
          mmir: answerMode === 'mmir' ? 'loading' : 'idle',
          web: answerMode === 'web' ? 'loading' : 'idle',
        },
        modeErrors: {},
        modeNeedsReauth: {},
      },
    ]);
    setLastImage(photo);
    setQuery('');
    setImage(null);
    setHint('');
    const requestedModes = ANSWER_MODES.filter(({ key }) => key === answerMode);
    void Promise.all(requestedModes.map(({ key }) => (
      runAnswerMode(id, text, photo, model, key)
    )));
  }, [answerMode, canSend, effectiveImage, model, query, runAnswerMode]);

  const retry = useCallback((turn) => {
    const activeMode = turn.activeMode || 'mmir';
    if (!turn.retryImage || turn.modeStatus?.[activeMode] === 'loading') return;
    setTurns((previous) => previous.map((candidate) => (
      candidate.id === turn.id
        ? {
          ...candidate,
          modeStatus: { ...candidate.modeStatus, [activeMode]: 'loading' },
          modeErrors: { ...candidate.modeErrors, [activeMode]: '' },
          modeNeedsReauth: { ...candidate.modeNeedsReauth, [activeMode]: false },
        }
        : candidate
    )));
    void runAnswerMode(
      turn.id,
      turn.query,
      turn.retryImage,
      turn.model,
      activeMode,
    );
  }, [runAnswerMode]);

  const selectAnswerMode = useCallback((turn, selectedAnswerMode) => {
    if (selectedAnswerMode === turn.activeMode) return;
    setAnswerMode(selectedAnswerMode);
    const shouldLoad = turn.modeStatus?.[selectedAnswerMode] === 'idle';
    updateTurn(turn.id, {
      activeMode: selectedAnswerMode,
      ...(shouldLoad
        ? {
          modeStatus: {
            ...turn.modeStatus,
            [selectedAnswerMode]: 'loading',
          },
        }
        : {}),
    });
    if (shouldLoad) {
      void runAnswerMode(
        turn.id,
        turn.query,
        turn.retryImage,
        turn.model,
        selectedAnswerMode,
      );
    }
  }, [runAnswerMode, updateTurn]);

  const newChat = useCallback(() => {
    if (client && typeof client.resetSession === 'function') {
      client.resetSession();
    }
    setTurns([]);
    setQuery('');
    setImage(null);
    setLastImage(null);
    setHint('');
  }, [client]);

  const selectSuggestion = useCallback((suggestion) => {
    setQuery(suggestion.prompt);
    if (!image) setSheetOpen(true);
  }, [image]);

  const empty = turns.length === 0;
  const selectedModel = modelOptions.find((option) => option.key === model)
    || modelOptions[0];
  const canChooseModel = modelOptions.length > 1;

  return (
    <main
      className={`lens-app ${dragging ? 'lens-dragging' : ''}`}
      onPaste={pasteImage}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
      }}
      onDrop={dropImage}
    >
      <div className="lens-column">
        <header className="lens-topbar">
          <button
            className="lens-frost lens-round-button"
            type="button"
            aria-label="Account"
            aria-expanded={accountOpen}
            onClick={() => {
              setModelOpen(false);
              setAccountOpen((open) => !open);
            }}
          >
            <User size={20} />
          </button>

          <button
            className="lens-frost lens-title-pill"
            type="button"
            aria-label={canChooseModel
              ? `Choose model, current model ${selectedModel.title}`
              : `Model ${selectedModel.title}`}
            aria-expanded={canChooseModel ? modelOpen : undefined}
            onClick={() => {
              if (!canChooseModel) return;
              setAccountOpen(false);
              setModelOpen((open) => !open);
            }}
          >
            <strong>{APP_TITLE}</strong>
            <span>{selectedModel.title}</span>
            {canChooseModel && <ChevronDown size={15} aria-hidden="true" />}
          </button>

          <button
            className="lens-frost lens-round-button"
            type="button"
            aria-label="New chat"
            disabled={empty && !query && !image}
            onClick={newChat}
          >
            <SquarePen size={20} />
          </button>

          {accountOpen && (
            <div className="lens-frost lens-account-menu">
              <span>{alias || 'Signed in'}</span>
              <button type="button" onClick={onSignOut}><LogOut size={17} /> Sign out</button>
            </div>
          )}

          {canChooseModel && modelOpen && (
            <div className="lens-frost lens-model-menu" role="menu" aria-label="Models">
              {modelOptions.map((option) => (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={option.key === model}
                  className={option.key === model ? 'lens-model-selected' : ''}
                  key={option.key}
                  onClick={() => {
                    setModel(option.key);
                    setModelOpen(false);
                  }}
                >
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.subtitle}</small>
                  </span>
                  {option.key === model && <span aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}
        </header>

        <section className={`lens-conversation ${empty ? 'lens-empty' : ''}`} ref={conversation}>
          {empty ? (
            <div className="lens-empty-copy">
              <h1>{APP_TITLE}</h1>
              <p>Ask anything about a photo</p>
            </div>
          ) : turns.map((turn) => (
            <article
              className="lens-turn"
              key={turn.id}
            >
              <div className="lens-user-row">
                <img src={turn.thumbnail} alt="Submitted attachment" />
                <div className="lens-user-bubble">{turn.query}</div>
              </div>
              <AssistantTurn
                turn={turn}
                onRetry={() => retry(turn)}
                onSignInAgain={onSignInAgain}
                onOpenSources={setActiveSources}
                onSelectMode={(selectedAnswerMode) => (
                  selectAnswerMode(turn, selectedAnswerMode)
                )}
              />
            </article>
          ))}
        </section>

        <div className="lens-bottom">
          {empty && (
            <div className="lens-suggestions" aria-label="Suggested questions">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  className="lens-suggestion"
                  type="button"
                  key={suggestion.title}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <strong>{suggestion.title}</strong>
                  <span>{suggestion.subtitle}</span>
                </button>
              ))}
            </div>
          )}

          {hint && <div className="lens-hint" role="alert">{hint}</div>}

          {effectiveImage && (
            <div className="lens-attachment">
              <img src={effectiveImage.thumbnail} alt="Selected" />
              <button
                type="button"
                aria-label="Remove selected photo"
                onClick={() => {
                  setImage(null);
                  setLastImage(null);
                }}
              >
                <X size={13} />
              </button>
            </div>
          )}

          <form className="lens-composer" onSubmit={submit}>
            <textarea
              aria-label="Question"
              placeholder="Ask about a photo"
              value={query}
              maxLength={MAX_QUERY_LENGTH}
              rows={1}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit(event);
                }
              }}
            />
            <button
              type="button"
              className="lens-icon-button"
              aria-label="Add photo"
              disabled={preparing}
              onClick={() => setSheetOpen(true)}
            >
              <Plus size={25} />
            </button>
            <button
              type="submit"
              className="lens-send"
              aria-label="Send"
              disabled={!canSend}
            >
              <ArrowUp size={22} />
            </button>
          </form>
        </div>
      </div>

      {dragging && (
        <div className="lens-drop-target" aria-hidden="true">
          <Images size={28} />
          <strong>Drop your photo</strong>
        </div>
      )}

      {sheetOpen && (
        <div className="lens-upload-layer" role="presentation">
          <button className="lens-modal-scrim" type="button" aria-label="Close" onClick={() => setSheetOpen(false)} />
          <div className="lens-upload-sheet" role="dialog" aria-modal="true" aria-label="Add a photo">
            <div className="lens-sheet-grabber" />
            <div className="lens-upload-heading">
              <span className="lens-eyebrow">Smart upload</span>
              <strong>Add a photo</strong>
            </div>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><Camera size={22} aria-hidden="true" /></span>
              <span><strong>Camera</strong><small>Take a new photo</small></span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                aria-label="Take photo"
                onChange={chooseImage}
              />
            </label>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><ImageIcon size={22} aria-hidden="true" /></span>
              <span><strong>Photos</strong><small>Choose from your library</small></span>
              <input
                type="file"
                accept="image/*"
                aria-label="Upload from library"
                onChange={chooseImage}
              />
            </label>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><FileUp size={22} aria-hidden="true" /></span>
              <span><strong>Files</strong><small>JPEG, PNG, or WebP</small></span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                aria-label="Choose from files"
                onChange={chooseImage}
              />
            </label>
            <button type="button" className="lens-upload-action" onClick={readClipboardImage}>
              <span className="lens-upload-icon"><ClipboardPaste size={22} aria-hidden="true" /></span>
              <span><strong>Paste image</strong><small>Use a copied screenshot or photo</small></span>
            </button>
            <button type="button" className="lens-sheet-cancel" onClick={() => setSheetOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {activeSources && (
        <GroundingSheet
          grounding={activeSources}
          onClose={() => setActiveSources(null)}
        />
      )}
    </main>
  );
}

export default function Lens() {
  const stage = getStage() || 'DEV';
  const region = getRegion(stage) || 'us-west-2';
  const [authStatus, setAuthStatus] = useState('restoring');
  const [alias, setAlias] = useState('');
  const [authError, setAuthError] = useState('');
  const [lensSettings, setLensSettings] = useState(null);
  const [apiConfigLoaded, setApiConfigLoaded] = useState(false);

  const client = useMemo(() => {
    return lensSettings?.answerApiHost
      ? new LensClient(lensSettings.answerApiHost, Auth)
      : null;
  }, [lensSettings]);

  useEffect(() => {
    let active = true;
    setApiConfigLoaded(false);
    void resolveLensSettings(stage, region).then((settings) => {
      if (!active) return;
      setLensSettings(settings);
      setApiConfigLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [region, stage]);

  const restoreSession = useCallback(async () => {
    try {
      await Auth.currentSession();
      const user = await Auth.currentAuthenticatedUser();
      setAlias(user?.attributes?.preferred_username || user?.getUsername?.() || user?.username || '');
      setAuthError('');
      setAuthStatus('signedIn');
    } catch (error) {
      setAuthStatus('signedOut');
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = APP_TITLE;
    if (!apiConfigLoaded) return undefined;
    initializeAmplify(stage, region);
    const listener = ({ payload: { event } }) => {
      if (event === 'signIn' || event === 'cognitoHostedUI') void restoreSession();
      if (event === 'signOut') {
        setAlias('');
        setAuthStatus('signedOut');
      }
    };
    Hub.listen('auth', listener);
    void restoreSession();
    return () => {
      Hub.remove('auth', listener);
      document.title = previousTitle;
    };
  }, [apiConfigLoaded, lensSettings, region, restoreSession, stage]);

  const signIn = useCallback(async () => {
    setAuthError('');
    setAuthStatus('signingIn');
    try {
      rememberPostAuthPath('/lens');
      await Auth.federatedSignIn({ customProvider: 'FederateOIDC' });
    } catch (error) {
      setAuthError('Sign-in could not be started. Please try again.');
      setAuthStatus('signedOut');
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthError('');
    try {
      await Auth.signOut();
    } catch (error) {
      setAuthError('Sign-out could not be completed. Your local session was cleared.');
    } finally {
      setAlias('');
      setAuthStatus('signedOut');
    }
  }, []);

  const signInAgain = useCallback(async () => {
    await signOut();
    await signIn();
  }, [signIn, signOut]);

  if (authStatus === 'restoring' || (authStatus === 'signedIn' && !apiConfigLoaded)) {
    return (
      <main className="lens-app lens-auth-screen" aria-live="polite">
        <div className="lens-spinner" aria-hidden="true" />
        <p>{authStatus === 'restoring' ? 'Restoring your session…' : 'Connecting to Lens…'}</p>
      </main>
    );
  }

  if (authStatus !== 'signedIn') {
    return (
      <main className="lens-app lens-auth-screen">
        <div className="lens-auth-copy">
          <h1>{APP_TITLE}</h1>
          <p>Ask anything about a photo.</p>
        </div>
        <button className="lens-auth-button" type="button" disabled={authStatus === 'signingIn'} onClick={signIn}>
          {authStatus === 'signingIn' ? 'Opening Midway…' : 'Continue with Midway'}
        </button>
        {authError && <p className="lens-auth-error" role="alert">{authError}</p>}
      </main>
    );
  }

  if (!client) {
    return (
      <main className="lens-app lens-auth-screen">
        <p role="alert">IMAX Web Lens is not configured for this environment.</p>
      </main>
    );
  }

  return (
    <LensExperience
      alias={alias}
      client={client}
      modelOptions={lensModelOptionsForStage(stage)}
      onSignOut={signOut}
      onSignInAgain={signInAgain}
    />
  );
}
